import React, { useId, useState, useRef } from 'react';
import './FileUpload.css';

const FileUpload = ({
  // Props
  acceptedFormats = [], // ['jpg', 'png', 'pdf'] etc
  maxFileSize = null, // in MB
  minFileSize = null, // in MB
  returnType = 'file', // 'file' or 'base64'
  multiple = false, // allow multiple files
  maxFiles = 5, // max number of files if multiple is true
  onFileSelect = () => {}, // callback function
  onError = () => {}, // error callback
  disabled = false,
  showPreview = true, // show file preview - NEW PROP CONTROL
  showFileList = true, // show uploaded files list - NEW PROP CONTROL
  autoUpload = true, // auto trigger callback on file select
  className = '',
  style = {}
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [inlineError, setInlineError] = useState('');
  const fileInputRef = useRef(null);
  const inputId = useId();

  // Convert file to base64 - returns pure base64 without prefix
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove data:*/*;base64, prefix to return pure base64
        const base64String = reader.result;
        const base64Data = base64String.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Get file extension
  const getFileExtension = (fileName) => {
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot === -1) return '';
    return fileName.substring(lastDot + 1).toLowerCase();
  };

  // Validate file
  const validateFile = (file) => {
    const errors = [];
    
    // Check file format
    if (acceptedFormats.length > 0) {
      const fileExtension = getFileExtension(file.name);
      if (!acceptedFormats.map(ext => ext.toLowerCase()).includes(fileExtension)) {
        errors.push(`File format not allowed. Accepted formats: ${acceptedFormats.join(', ')}`);
      }
    }
    
    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    
    if (maxFileSize && fileSizeInMB > maxFileSize) {
      errors.push(`File size exceeds maximum limit of ${maxFileSize}MB`);
    }
    
    if (minFileSize && fileSizeInMB < minFileSize) {
      errors.push(`File size is below minimum limit of ${minFileSize}MB`);
    }
    
    return errors;
  };

  // Process files
  const processFiles = async (fileList) => {
    const newFiles = Array.from(fileList);
    const validFiles = [];
    const filePreviews = [];
    const validationMessages = [];

    // Check max files limit
    if (multiple && files.length + newFiles.length > maxFiles) {
      const message = `Maximum ${maxFiles} files allowed`;
      setInlineError(message);
      onError({
        type: 'maxFiles',
        message,
        files: []
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate and process each file
    for (const file of newFiles) {
      const errors = validateFile(file);
      
      if (errors.length > 0) {
        const message = errors.join(', ');
        validationMessages.push(message);
        onError({
          type: 'validation',
          message,
          file: file.name,
          errors: errors
        });
      } else {
        validFiles.push(file);
        
        // Generate preview for images if showPreview is true
        if (showPreview) {
          if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              filePreviews.push({ 
                name: file.name, 
                url: reader.result, 
                type: 'image' 
              });
            };
          } else {
            filePreviews.push({ 
              name: file.name, 
              url: null, 
              type: getFileIcon(file.name) 
            });
          }
        }
      }
    }

    if (validationMessages.length > 0) {
      setInlineError(validationMessages[0]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Single-file mode: reject replaces any prior selection
      if (!multiple) {
        setFiles([]);
        setPreviews([]);
      }
    } else {
      setInlineError('');
    }

    if (validFiles.length > 0) {
      const updatedFiles = multiple ? [...files, ...validFiles] : validFiles;
      setFiles(updatedFiles);
      
      if (showPreview) {
        // Wait for all previews to load
        setTimeout(() => {
          setPreviews(multiple ? [...previews, ...filePreviews] : filePreviews);
        }, 100);
      }

      // Return files based on returnType
      if (autoUpload) {
        if (returnType === 'base64') {
          const base64Files = await Promise.all(
            validFiles.map(async (file) => ({
              name: file.name,
              size: file.size,
              type: file.type,
              extension: getFileExtension(file.name), // Added extension
              data: await fileToBase64(file) // Pure base64 data
            }))
          );
          onFileSelect(multiple ? base64Files : base64Files[0]);
        } else {
          const fileObjects = validFiles.map(file => ({
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            extension: getFileExtension(file.name) // Added extension
          }));
          onFileSelect(multiple ? fileObjects : fileObjects[0]);
        }
      }
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      processFiles(fileList);
    }
    // Allow re-selecting the same rejected file later
    e.target.value = '';
  };

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (!disabled) {
      const fileList = e.dataTransfer.files;
      if (fileList && fileList.length > 0) {
        processFiles(fileList);
      }
    }
  };

  // Remove file
  const removeFile = async (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    
    if (autoUpload) {
      if (returnType === 'base64') {
        const base64Files = await Promise.all(
          updatedFiles.map(async (file) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            extension: getFileExtension(file.name), // Added extension
            data: await fileToBase64(file) // Pure base64 data
          }))
        );
        onFileSelect(multiple ? base64Files : (base64Files[0] || null));
      } else {
        const fileObjects = updatedFiles.map(file => ({
          file: file,
          name: file.name,
          size: file.size,
          type: file.type,
          extension: getFileExtension(file.name) // Added extension
        }));
        onFileSelect(multiple ? fileObjects : (fileObjects[0] || null));
      }
    }
  };

  // Get file icon based on extension
  const getFileIcon = (fileName) => {
    const ext = getFileExtension(fileName);
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['xls', 'xlsx'].includes(ext)) return 'excel';
    if (['pdf'].includes(ext)) return 'pdf';
    if (['txt'].includes(ext)) return 'text';
    if (['zip', 'rar', '7z'].includes(ext)) return 'archive';
    return 'file';
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Upload files manually (if autoUpload is false)
  const uploadFiles = async () => {
    if (files.length === 0) {
      onError({
        type: 'noFiles',
        message: 'No files selected',
        files: []
      });
      return;
    }

    if (returnType === 'base64') {
      const base64Files = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          extension: getFileExtension(file.name), // Added extension
          data: await fileToBase64(file) // Pure base64 data
        }))
      );
      onFileSelect(multiple ? base64Files : base64Files[0]);
    } else {
      const fileObjects = files.map(file => ({
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        extension: getFileExtension(file.name) // Added extension
      }));
      onFileSelect(multiple ? fileObjects : fileObjects[0]);
    }
  };

  // Clear all files
  const clearAll = () => {
    setFiles([]);
    setPreviews([]);
    setInlineError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect(multiple ? [] : null);
  };

  // Generate accept string for input
  const getAcceptString = () => {
    if (acceptedFormats.length === 0) return '*';
    return acceptedFormats.map(ext => `.${ext}`).join(',');
  };

  return (
    <div className={`file-upload-container ${className}`} style={style}>
      <div
        className={`file-upload-area ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="upload-icon">
          <svg width="50" height="50" viewBox="0 0 24 24" fill="#2196F3">
            <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
          </svg>
        </div>

        <div className="upload-controls">
          <input
            ref={fileInputRef}
            type="file"
            id={inputId}
            className="file-input-hidden"
            onChange={handleFileSelect}
            accept={getAcceptString()}
            multiple={multiple}
            disabled={disabled}
          />
          
          <label htmlFor={inputId} className="file-input-label">
            Choose Files
          </label>
          
          <span className="file-status">
            {files.length > 0 
              ? `${files.length} file${files.length > 1 ? 's' : ''} selected`
              : 'No file chosen'
            }
          </span>
        </div>

        <p className="upload-text">
          or Drag And Drop Files Here To Upload.
        </p>

        {acceptedFormats.length > 0 && (
          <p className="accepted-formats">
            Accepted formats: {acceptedFormats.join(', ')}
          </p>
        )}

        {(maxFileSize || minFileSize) && (
          <p className="file-size-info">
            {minFileSize && `Min: ${minFileSize}MB`}
            {minFileSize && maxFileSize && ' | '}
            {maxFileSize && `Max: ${maxFileSize}MB`}
          </p>
        )}

        {inlineError ? (
          <p className="file-upload-inline-error" role="alert">
            {inlineError}
          </p>
        ) : null}
      </div>

      {/* File List - Only show if showFileList prop is true */}
      {showFileList && files.length > 0 && (
        <div className="file-list">
          <div className="file-list-header">
            <h4>Uploaded Files</h4>
            <button className="clear-all-btn" onClick={clearAll}>
              Clear All
            </button>
          </div>
          
          {files.map((file, index) => (
            <div key={index} className="file-item">
              {/* Preview - Only show if showPreview prop is true */}
              {showPreview && previews[index] && (
                <div className="file-preview">
                  {previews[index].type === 'image' ? (
                    <img src={previews[index].url} alt={file.name} />
                  ) : (
                    <div className={`file-icon ${previews[index].type}`}>
                      {previews[index].type.toUpperCase()}
                    </div>
                  )}
                </div>
              )}
              
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-size">{formatFileSize(file.size)}</div>
              </div>
              
              <button 
                className="remove-btn"
                onClick={() => removeFile(index)}
                title="Remove file"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Manual Upload Button */}
      {!autoUpload && files.length > 0 && (
        <div className="upload-actions">
          <button className="upload-btn" onClick={uploadFiles}>
            Upload Files
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;